/**
 * ScriptSection — collapsible section (Pinned/Recent) for the Scripts panel.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { AnimatedCollapse } from '../../workspace/start-page/components/AnimatedCollapse';
import { ScriptListItem } from './ScriptListItem';
import type { ScriptEntry } from '../types/scriptBrowserTypes';

type ScriptSectionProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  scripts: ScriptEntry[];
  isPinned?: boolean;
  isActive: (script: ScriptEntry) => boolean;
  onOpen: (script: ScriptEntry) => void;
  onTogglePin?: (script: ScriptEntry) => void;
  focusedScriptId?: string | null;
  testId: string;
  /** Id of the currently active connection, or null/undefined when nothing
   *  is connected. Threaded down to each row so it can suppress its server
   *  sublabel by connection identity, not display name (PP-29) — display
   *  names are not unique across saved connections. */
  activeConnectionId?: string | null;
  /** Display name of the currently active connection. Used only as a
   *  fallback for scripts saved before `connectionId` was tracked. */
  activeConnectionName?: string | null;
};

export function ScriptSection({
  icon: Icon,
  label,
  count,
  isExpanded,
  onToggle,
  scripts,
  isPinned,
  isActive,
  onOpen,
  onTogglePin,
  focusedScriptId,
  testId,
  activeConnectionId,
  activeConnectionName,
}: ScriptSectionProps) {
  return (
    <div className="mb-1" data-testid={testId}>
      <Button
        variant="ghost"
        size="sm"
        className="w-full flex items-center justify-start gap-1.5 h-7 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
        onClick={onToggle}
        data-testid={`${testId}-toggle`}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" data-testid={`${testId}-chevron`} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" data-testid={`${testId}-chevron`} />
        )}
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span>{label}</span>
        <span>({count})</span>
      </Button>
      <AnimatedCollapse isOpen={isExpanded}>
        <div className="py-0.5">
          {scripts.map((script) => (
            <ScriptListItem
              key={script.id}
              script={script}
              isPinned={isPinned}
              isActive={isActive(script)}
              isFocused={focusedScriptId === script.id}
              onOpen={onOpen}
              onTogglePin={onTogglePin}
              activeConnectionId={activeConnectionId}
              activeConnectionName={activeConnectionName}
            />
          ))}
        </div>
      </AnimatedCollapse>
    </div>
  );
}
